import React, { FC, useEffect } from "react";
import { debounce } from "lodash";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import {
  AuthService,
  CustomButton,
  ITableHeaders,
  Loader,
  TableHeader,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  RolesAndPermissionStore,
  SubscriptionStore,
} from "../../../shared/store";
import { SubscriptionGridModel } from "../../../shared/models";
import { SubscriptionUtil } from "../../../shared/utils";
import {  SubscriptionService } from "../../../shared/services";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ItemContentArea,
  TableGrid,
} from "../../../shared/components";
import SubscriptionBillingEnds from "./SubscriptionBillingEnds";
import { GridNames, ItemDropDownOptions } from "../../../shared/enums";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  subscriptionStore: SubscriptionStore;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const SubscriptionGrid: FC<IProps> = ({
  subscriptionStore,
  navigate,
  itemStore,
  merchantAccountStore,
  rolesAndPermissionStore,
}) => {
  const [showLoader, setShowLoader] = React.useState(true);
  const [firstTimeLoad, setFirstTimeLoad] = React.useState(true);

  useEffect(() => {
    itemStore.updateSearchValue("", ItemDropDownOptions.Subscriptions);
    loadData(
      subscriptionStore.pagination.pageNumber,
      subscriptionStore.pagination.pageSize,
      subscriptionStore.searchValue,
      true
    );

    const subscription: Subscription = subscriptionStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        loadData(
          subscriptionStore.pagination.pageNumber,
          subscriptionStore.pagination.pageSize,
          subscriptionStore.searchValue
        );
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  function loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    setFirstTimeLoad(firstTimeLoad);
    setShowLoader(true);
    SubscriptionService.getSubscriptions(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        setShowLoader(false);
        subscriptionStore.deserializeGridData(
          response.result || [],
          response.meta
        );
      },
    });
  }

  function onPaginationChange(pageNumber: number, pageSize: number) {
    loadData(pageNumber, pageSize, subscriptionStore.searchValue);
  }

  function goToDetailPage(id: string) {
    navigate(`/app/subscriptions/detail/${id}`);
  }

  const loadSubscriptionData = () => {
    loadData(
      subscriptionStore.pagination.pageNumber,
      subscriptionStore.pagination.pageSize,
      subscriptionStore.searchValue
    );
  };

  const isNoData =
    firstTimeLoad &&
    subscriptionStore.searchValue === "" &&
    !showLoader &&
    subscriptionStore.pagination.totalItemCount === 0;

  function renderDetails(node: React.ReactNode, showItemRightContent = true) {
    const content = showItemRightContent ? (
      <DashboardTopRightSide
        itemStore={itemStore}
        showSearchInput={!isNoData}
        showButton={!isNoData}
        onRefresh={() => loadSubscriptionData()}
      />
    ) : (
      <></>
    );
    return (
      <>
        <DashboardTopBar
          itemStore={itemStore}
          rightContent={<>{showLoader && firstTimeLoad ? null : content}</>}
          merchantAccountStore={merchantAccountStore}
          rolesAndPermissionStore={rolesAndPermissionStore}
        />
        <ItemContentArea
          itemStore={itemStore}
          node={node}
          showLoader={showLoader}
          // className={isNoData ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  function columns() {
    const columns: ITableHeaders<SubscriptionGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: SubscriptionGridModel) => {
          const colors = SubscriptionUtil.getColorsByStatus(data.status);
          return (
            <CustomButton
              bgColor={colors.bgColor}
              color={colors.color}
              className="status-btn"
              // minWidth="70px"
            >
              {data.status}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "contact",
        title: "Contact",
        cellColor: "#556bb8",
        cellClassName: "clickable",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: SubscriptionGridModel) => {
          if (!data.contact) return null;
          return (
            <span
              onClick={() =>
                navigate(`/app/contacts/detail/${data.contact?.id}`)
              }
            >
              {data.contact?.displayValue}
            </span>
          );
        },
      },
      {
        dataKey: "nextAmount",
        title: "Next Amount",
        className: "table-col-right",
        cellClassName: "table-col-right",

        onCellRender: (data: SubscriptionGridModel) => {
          return "$" + AccountSettingsStore.formatAmount(data.nextAmount);
        },
      },
      {
        dataKey: "billingInterval",
        title: "Bill Every",
        onCellRender: (data: SubscriptionGridModel) => {
          return (
            data.billingIntervalCount +
            " " +
            data.billingInterval +
            (data.billingIntervalCount > 1 ? "s" : "")
          );
        },
      },
      {
        dataKey: "nextDue",
        title: "Next Due",
        onCellRender: (data: SubscriptionGridModel) => {
          return UtilityMethods.formatDateInString(data.nextDue);
        },
      },
      {
        dataKey: "billingEndsInterval",
        title: "Ends",
        onCellRender: (data: SubscriptionGridModel) => {
          return (
            <SubscriptionBillingEnds
              billingEnds={data.billingEndsInterval}
              billingEndIntervalCount={data.billingEndIntervalCount}
              billingEndsAfterInterval={data.billingEndsAfterInterval}
            />
          );
        },
      },
    ];
    return columns;
  }

  return renderDetails(
    <TableGrid
      tableHeader={
        <TableHeader
          showFilter={subscriptionStore.showFilter}
          onToggleFilter={() => subscriptionStore.toggleFilter()}
          total={subscriptionStore.pagination.totalItemCount}
        />
      }
      totalItemCount={subscriptionStore.pagination.totalItemCount}
      onRowClick={(data: SubscriptionGridModel) => goToDetailPage(data.id)}
      cellSpacing={20}
      onPaginationChange={(pageNumber: number, pageSize: number) =>
        onPaginationChange(pageNumber, pageSize)
      }
      pagination={subscriptionStore.pagination}
      data={subscriptionStore.gridData}
      headers={columns()}
      name={GridNames.Subscription}
      height={
        subscriptionStore.showFilter
          ? "calc(100vh - 499px)"
          : merchantAccountStore.hasNotificationBar()
            ? "calc(100vh - 350px)"
            : ""
      }
      hideTable={isNoData}
      onRefresh={() => loadSubscriptionData()}
      rolesAndPermissionStore={rolesAndPermissionStore}
      countriesWithState={itemStore.countryStore.countriesWithState}
    />
  );
};

export default observer(SubscriptionGrid);
